/**
 * ObservabilityService
 * 
 * Centralized service for transporting observability data to backend systems.
 * Handles communication with Faro SDK for comprehensive observability.
 * 
 * Data formatting is handled by specific formatters (e.g., PerformanceMetricsFormatter).
 */

import { faroService } from './FaroService';

export interface ObservabilityPayload {
  timestamp: string;
  service: string;
  type: string;
  context: Record<string, any>;
  data: Record<string, any>;
}

export interface ObservabilityConfig {
  enabled: boolean;
  endpoint?: string;
  apiKey?: string;
  batchSize?: number;
  flushInterval?: number;
}

export class ObservabilityService {
  private _config: ObservabilityConfig;

  constructor(config: ObservabilityConfig = { enabled: true }) {
    this._config = {
      batchSize: 10,
      flushInterval: 30000, // 30 seconds
      ...config
    };
    
    console.log('[ObservabilityService] Initialized', { 
      enabled: this._config.enabled,
      batchSize: this._config.batchSize,
    });
  }

  /**
   * Report observability data to backend systems (via Faro SDK)
   */
  async reportData(payload: ObservabilityPayload): Promise<void> {
    if (!this._config.enabled) {
      return;
    }

    try {
      // Validate payload before processing
      if (!this._validatePayload(payload)) {
        console.warn('[ObservabilityService] Invalid payload, skipping:', payload);
        return;
      }

      // Send to Faro SDK for comprehensive observability
      await this._reportToFaro(payload);
      
    } catch (error) {
      console.error('[ObservabilityService] Failed to report data:', error);
      // Don't throw - observability failures shouldn't break the main flow
    }
  }

  /**
   * Report data to Faro SDK (for automatic RUM + custom events)
   */
  private async _reportToFaro(payload: ObservabilityPayload): Promise<void> {
    if (!faroService.isReady()) {
      console.warn('[ObservabilityService] [FARO] Faro service not ready, skipping');
      return;
    }

    try {
      // For performance metrics, send as custom measurements to Faro
      if (payload.type === 'performance_metrics' && payload.data.latency) {
        const latencyData = payload.data.latency;
        
        // console.log('[ObservabilityService] [FARO] Processing performance metrics:', {
        //   latencyKeys: Object.keys(latencyData),
        //   service: payload.service,
        //   sessionType: payload.context.sessionType,
        //   sourceId: payload.context.sourceId
        // });
        
        // Send ALL latency metrics to Faro (not just hardcoded ones)
        Object.entries(latencyData).forEach(([metricName, metricData]) => {
          if (metricName === 'overallHealth') {
            // Handle overall health separately
            const healthData = metricData as any;
            if (healthData && typeof healthData.score === 'number') {
              // console.log('[ObservabilityService] [FARO] Sending health score:', {
              //   score: healthData.score,
              //   grade: healthData.grade
              // });
              faroService.trackMeasurement('stt_health_score', healthData.score, {
                service: payload.service,
                sessionType: payload.context.sessionType,
                sourceId: payload.context.sourceId,
                grade: healthData.grade || 'unknown',
                processName: payload.context.processName || 'unknown',
              });
            }
          } else if (metricData && typeof metricData === 'object' && 'latency' in metricData) {
            // Handle all latency metrics (STT, WebRTC, etc.)
            const latency = metricData as any;
            if (typeof latency.latency === 'number') {
              const measurementName = `stt_${metricName}_ms`;
              // console.log('[ObservabilityService] [FARO] Sending latency measurement:', {
              //   name: measurementName,
              //   value: latency.latency,
              //   status: latency.status,
              //   processName: payload.context.processName
              // });
              faroService.trackMeasurement(measurementName, latency.latency, {
                service: payload.service,
                sessionType: payload.context.sessionType,
                sourceId: payload.context.sourceId,
                status: latency.status || 'unknown',
                target: latency.target || 0,
                percentage: latency.percentage || 0,
                processName: payload.context.processName || 'unknown',
              });
            }
          }
        });

        // Legacy support for old metric names (keep for backwards compatibility)
        if (latencyData.pageLoad) {
          faroService.trackMeasurement('page_load_time', latencyData.pageLoad, {
            service: payload.service,
            sessionType: payload.context.sessionType,
            roomId: payload.context.roomId ? 'present' : 'none',
          });
        }

        if (latencyData.apiResponse) {
          faroService.trackMeasurement('api_response_time', latencyData.apiResponse, {
            service: payload.service,
            sessionType: payload.context.sessionType,
          });
        }

        // Send WebRTC connection metrics if present (legacy format)
        if (payload.data.webrtc) {
          faroService.trackMeasurement('webrtc_connection_time', payload.data.webrtc.connectionTime || 0, {
            service: payload.service,
            sessionType: payload.context.sessionType,
            connectionState: payload.data.webrtc.connectionState,
          });
        }
      }

      // For business events, send as custom events to Faro
      if (payload.type === 'business_event') {
        faroService.trackEvent(payload.data.eventName || 'unknown_business_event', {
          service: payload.service,
          sessionType: payload.context.sessionType,
          ...payload.data.attributes
        });
      }

      // console.log(`[ObservabilityService] [FARO] Successfully sent ${payload.type} to Faro SDK`);
    } catch (error) {
      console.error('[ObservabilityService] [FARO] Failed to send to Faro:', error);
    }
  }

  /**
   * Set user context for session tracking (hybrid)
   */
  setUserContext(userId: string, attributes: Record<string, any> = {}): void {
    if (faroService.isReady()) {
      faroService.setUser(userId, attributes);
    }
    // console.log('[ObservabilityService] User context set:', { userId, attributes });
  }

  /**
   * Track business event (hybrid approach)
   */
  trackBusinessEvent(eventName: string, attributes: Record<string, any> = {}): void {
    const payload: ObservabilityPayload = {
      timestamp: new Date().toISOString(),
      service: 'telegamez-frontend',
      type: 'business_event',
      context: {
        sessionType: attributes.sessionType || 'unknown',
        userId: attributes.userId,
        roomId: attributes.roomId,
      },
      data: {
        eventName,
        attributes: { ...attributes }
      }
    };

    this.reportData(payload);
  }

  /**
   * Validate payload structure and content
   */
  private _validatePayload(payload: ObservabilityPayload): boolean {
    // Check required fields
    if (!payload.timestamp || !payload.service || !payload.type) {
      return false;
    }

    // Validate timestamp format
    if (isNaN(Date.parse(payload.timestamp))) {
      return false;
    }

    // Check data structure
    if (!payload.data || typeof payload.data !== 'object') {
      return false;
    }

    // Validate context structure
    if (!payload.context || typeof payload.context !== 'object') {
      return false;
    }

    // Performance metrics specific validation
    if (payload.type === 'performance_metrics') {
      if (!payload.data.latency || typeof payload.data.latency !== 'object') {
        return false;
      }
    }

    return true;
  }

  /**
   * Update configuration (useful for enabling/disabling reporting)
   */
  updateConfig(newConfig: Partial<ObservabilityConfig>): void {
    this._config = { ...this._config, ...newConfig };
    // console.log('[ObservabilityService] Configuration updated', this._config);
  }

  /**
   * Get current configuration
   */
  getConfig(): ObservabilityConfig {
    return { ...this._config };
  }
}

// Export singleton instance
export const observabilityService = new ObservabilityService({
  enabled: process.env.NODE_ENV !== 'test' // Disable in tests by default
});

 